import { useTheme } from '@mui/material';
import MuiPopover, {
    PopoverOrigin,
    PopoverProps as MuiPopoverProps
} from '@mui/material/Popover';
import { debounce } from 'essential-utils';
import * as React from 'react';

import { Box, BoxProps } from '../box';
import { withPaperElevation } from '../paper';
import { withVersion } from '../utils';

export * from './with-tooltip';

export { type PopoverActions } from '@mui/material/Popover';

export interface PopoverProps {
    open?: boolean;
    label?: React.ReactNode;
    /** @default click */
    behaviour?: 'click' | 'hover';
    hideBackdrop?: boolean;
    disabled?: boolean;
    disableAutoFocus?: boolean;
    disableEnforceFocus?: boolean;
    children?: React.ReactNode;
    transformOrigin?: Partial<PopoverOrigin>;
    anchorOrigin?: Partial<PopoverOrigin>;
    anchorEl?: HTMLElement;
    sx?: MuiPopoverProps['sx'];
    action?: MuiPopoverProps['action'];
    slotProps?: MuiPopoverProps['slotProps'];

    onOpen?(): void;
    onClose?(): void;
}

const PopoverImpl = React.forwardRef<HTMLDivElement, PopoverProps>(
    (props, ref) => {
        const {
            behaviour = 'click',
            hideBackdrop: _hideBackdrop,
            onClose,
            sx,
            slotProps
        } = props;
        const hideBackdrop =
            behaviour === 'hover' ? true : (_hideBackdrop ?? false);
        const { spacing } = useTheme();
        const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(
            null
        );
        const labelRef = React.useRef<HTMLElement>(null);
        const open = props.open ?? Boolean(anchorEl);

        const onOpen = (event: React.MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            if (!props.anchorEl) {
                setAnchorEl(event.currentTarget);
            }
            props.onOpen?.();
        };

        const onOpenDebounced = React.useMemo(() => {
            return debounce(onOpen, 300);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [props.onOpen, props.anchorEl]);

        const onCloseDebounced = React.useMemo(() => {
            return debounce(() => onClose?.(), 150);
        }, [onClose]);

        React.useEffect(() => {
            if (props.anchorEl) {
                setAnchorEl(props.anchorEl);
            }
        }, [props.anchorEl]);

        React.useEffect(() => {
            return () => {
                onOpenDebounced.cancel();
                onCloseDebounced.cancel();
            };
        }, [onOpenDebounced, onCloseDebounced]);

        const labelEnterEvent = ((): React.HTMLAttributes<HTMLElement> => {
            switch (behaviour) {
                case 'click':
                    return { onClick: props.disabled ? undefined : onOpen };
                case 'hover':
                    return {
                        onMouseEnter: props.disabled
                            ? undefined
                            : onOpenDebounced
                    };
            }
        })();

        const labelLeaveEvent = ((): React.HTMLAttributes<HTMLElement> => {
            switch (behaviour) {
                case 'click':
                    return {};
                case 'hover':
                    return {
                        onMouseLeave: () => {
                            onOpenDebounced.cancel();
                            onCloseDebounced();
                        }
                    };
            }
        })();

        const label = props.label
            ? React.cloneElement(props.label as React.ReactElement, {
                  ref: labelRef,
                  ...labelEnterEvent,
                  ...labelLeaveEvent
              })
            : null;

        return (
            <>
                {label}
                <MuiPopover
                    ref={ref}
                    hideBackdrop={hideBackdrop}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                        ...props.anchorOrigin
                    }}
                    transformOrigin={{
                        vertical:
                            props.transformOrigin?.vertical ??
                            parseInt(spacing(-0.5), 10),
                        horizontal: 'center',
                        ...props.transformOrigin
                    }}
                    anchorEl={anchorEl ?? labelRef.current}
                    open={open}
                    onClose={onClose}
                    onMouseLeave={
                        behaviour === 'hover' ? onCloseDebounced : undefined
                    }
                    onMouseEnter={() => {
                        onOpenDebounced.cancel();
                        onCloseDebounced.cancel();
                    }}
                    disableAutoFocus={props.disableAutoFocus}
                    disableEnforceFocus={props.disableEnforceFocus}
                    action={props.action}
                    sx={{
                        ...(hideBackdrop
                            ? {
                                  pointerEvents: 'none',

                                  '> *': {
                                      pointerEvents: 'auto'
                                  }
                              }
                            : undefined),
                        ...sx
                    }}
                    slotProps={{
                        ...slotProps,
                        paper: withPaperElevation(slotProps?.paper, 'L')
                    }}
                >
                    {props.children}
                </MuiPopover>
            </>
        );
    }
);

export const Popover = withVersion(PopoverImpl, '1.0.0');

export interface PopoverLabelProps {
    open?: boolean;
    'aria-controls'?: string;
    'aria-expanded'?: 'true';
    'aria-haspopup'?: 'true';
}

// FIXME: THis causes the tests to fail:
// TypeError: Cannot read properties of undefined (reading '__emotion_styles')
// const PopoverLabelWrapper = styled(Box)({ width: 'max-content' });

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function usePopoverProps<P extends Record<string, any>>(options: {
    label: React.ComponentType<P & PopoverLabelProps>;
    labelProps?: P & PopoverLabelProps;
    labelWrapperProps?: TypedOmit<BoxProps, 'ref'>;
    disabled?: boolean;
    'aria-label'?: string;
    /** @default click */
    behaviour?: PopoverProps['behaviour'];
}) {
    const [open, setOpen] = React.useState(false);
    const labelRef = React.useRef(options.label);

    labelRef.current = options.label;

    const Label = React.useMemo(
        () => (props: P & PopoverLabelProps) => {
            const CurrentLabel = labelRef.current;

            return <CurrentLabel {...props} />;
        },
        [labelRef]
    );

    return {
        disabled: options.disabled,
        behaviour: options.behaviour,
        label: (
            <Box width="max-content" {...options.labelWrapperProps}>
                <Label
                    {...(options.labelProps as P)}
                    open={open}
                    aria-controls={open ? options['aria-label'] : undefined}
                    aria-expanded={open ? 'true' : undefined}
                    aria-haspopup="true"
                />
            </Box>
        ),
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false)
    };
}
